import _ from 'lodash';
import moment from 'moment-timezone';
import { ANNOTATION_TYPE, ID_PLACEHOLDER, MAX_NUMBER_OF_ANNOTATE_IDS } from '@/annotation/annotation.constants';
import { findExclusiveEntry, findJournalEntries } from '@/annotation/annotation.store';
import { setHtmlCKEditor } from '@/annotation/ckEditor.utilities';
import {
  sqAnnotationStore,
  sqDurationStore,
  sqTrendConditionStore,
  sqTrendStore,
  sqWorkbenchStore,
  sqWorkbookStore,
} from '@/core/core.stores';
import { parseDuration, parseISODate } from '@/datetime/dateTime.utilities';
import { API_TYPES } from '@/main/app.constants';
import { isWorksheet } from '@/main/routing.utilities';
import { cancelGroup } from '@/requests/pendingRequests.utilities';
import { canManageItem, canModifyDocument, canWriteItem } from '@/services/authorization.service';
import { emitComment, onComment } from '@/services/notifier.service';
import { getAllItems } from '@/trend/trendDataHelper.utilities';
import { recomputeRuntimeChartItems } from '@/trendData/capsuleRuntime.actions';
import { addTrendItem, fetchChartCapsules, fetchTableAndChartCapsules } from '@/trendData/trend.actions';
import { getCapsuleItems } from '@/trendData/trendCapsule.selectors';
import { fetchTimebarCapsules } from '@/trendData/trendCondition.actions';
import { getDefaultName } from '@/utilities/formula.utilities';
import { validateGuid } from '@/utilities/guid.utilities';
import { isCanceled } from '@/utilities/http.utilities';
import { createCapsuleLink, createItemLink } from '@/utilities/journalLink.utilities';
import { toggleEditor } from '@/utilities/migration.utilities';
import { handleForbidden, isApiError } from '@/utilities/redaction.utilities';
import { getCursorPosition, setCursorPosition } from '@/utilities/reportEditor.utilities';
import { subscribe } from '@/utilities/socket.utilities';
import { debounceAsync, isInWorkbookRouteAndWorkbookLoaded, isTrendable } from '@/utilities/utilities';
import { isPresentationWorkbookMode, isViewOnlyWorkbookMode } from '@/utilities/workbookMode.utilities';
import { tabsetChangeTab } from '@/worksheet/worksheet.actions';
import { sqAnnotationsApi } from '@seeqdev/api/endpoint/AnnotationsApi';
import { sqConditionsApi } from '@seeqdev/api/endpoint/ConditionsApi';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { SeeqNames } from '@seeqdev/constants';
import type { Annotation, AnnotationCondition } from '@/annotation/annotation.types';
import type { AnnotationInputV1 } from '@seeqdev/api/model/AnnotationInputV1';
import type { AnnotationOutputV1 } from '@seeqdev/api/model/AnnotationOutputV1';
import type { CapsuleV1 } from '@seeqdev/api/model/CapsuleV1';
import type { ItemOutputV1, ItemPreviewV1 } from '@seeqdev/api/model/models';

const CAPSULE_SET_PREFIX = 'Annotation Capsules';
let highlightCanceller: string | undefined | NodeJS.Timeout;

// Built on first call, not at module load: this module sits in a circular import with @/utilities/utilities, so
// debounceAsync can be undefined mid-evaluation.
let debouncedOnJournalUpdateFn: (() => void) | undefined;
export function debouncedOnJournalUpdate(): void {
  debouncedOnJournalUpdateFn ??= debounceAsync(onJournalUpdate);
  debouncedOnJournalUpdateFn();
}

/**
 * Subscribes to updates to the given journal entry, so that changes made elsewhere (SPy, GenAI, another user)
 * are pulled into the open editor.
 *
 * @param journalId - ID of the journal annotation
 * @returns the function that tears this subscription down
 */
export function subscribeToJournal(journalId: string) {
  return subscribe({
    channelId: [SeeqNames.Channels.JournalUpdateChannel, journalId],
    onMessage: debouncedOnJournalUpdate,
    // Fires on the first subscribe as well as on reconnect, and we want the fetch either way: the store's copy of
    // the document is loaded with the worksheet and can already be stale by the time the journal is opened, and
    // updates published while the socket was down were missed entirely.
    onSubscribe: () => debouncedOnJournalUpdate(),
  });
}

/**
 * Displays a new blank journal entry ready for editing
 */
export function newEntry() {
  setId(ID_PLACEHOLDER);
  setDocument('');
  setIsDiscoverable(false);
}

/**
 * Creates a new annotation, which is effectively a new journal entry with signal, condition, and capsule
 * links automatically inserted, and sets focus to the start of the entry.
 *
 * @param annotationCondition - Optional annotation condition from useAnnotationCondition() hook.
 */
export function newAnnotation(annotationCondition?: AnnotationCondition) {
  let promise = Promise.resolve();
  const links: string[] = [];

  links.push('<p><br></p>');

  _.forEach(_.filter(getAllItems({}), 'selected'), (item) => {
    links.push(`<p>${createItemLink(item)}</p>`);
  });

  _.forEach(_.filter(getCapsuleItems(), 'selected'), (capsule: any) => {
    links.push(
      `<p>${
        createCapsuleLink({
          conditionId: capsule.isChildOf,
          start: capsule.startTime,
          end: capsule.endTime,
        }).link
      }</p>`,
    );
  });

  if (sqTrendStore.isRegionSelected) {
    promise = getUserConditionInterests(
      [
        {
          start: moment.utc(sqTrendStore.selectedRegion.min).toISOString(),
          end: moment.utc(sqTrendStore.selectedRegion.max).toISOString(),
        },
      ],
      annotationCondition,
    ).then((interests) => {
      _.forEach(interests, (interest: any) => {
        links.push(
          `<p>${
            createCapsuleLink({
              conditionId: interest.interestId,
              start: sqTrendStore.selectedRegion.min,
              end: sqTrendStore.selectedRegion.max,
            }).link
          }</p>`,
        );
      });
    });
  }

  promise.then(() => {
    const document = links.join('');
    setId(ID_PLACEHOLDER);
    setDocument(document, true);
    setIsDiscoverable(true);
    showJournalTab();
  });
}

/**
 * Displays a journal entry in view mode
 *
 * @param {String} id - The id of the annotation
 * @param {Boolean} [exposeJournalTab = true] - optional argument that allows the default behavior of exposing the
 * journal tab to be overridden.
 */
export function showEntry(id: string, exposeJournalTab = true) {
  return fetchDocument(id)
    .then((result) => {
      if (!result.ckEnabled) {
        return toggleEditor(id).then((document) => ({
          ...result,
          document,
          ckEnabled: true,
        }));
      } else {
        return result;
      }
    })
    .then((result) => {
      setId(id);
      setIsDiscoverable(result.discoverable);
      setIsCkEnabled(!!result.ckEnabled);
      setDocument(result.document ?? '');
    })
    .then(() => {
      if (exposeJournalTab) {
        showJournalTab();
      }
    });
}

/**
 * Adds an Annotation by adding its interests and switching the tab to show the annotation.
 *
 * @param item - An annotation from the REST API.
 * @return A promise that will be fulfilled when it is finished adding the annotation.
 */
export async function addAnnotation(item: ItemOutputV1) {
  const { data: annotation } = await sqAnnotationsApi.getAnnotation({ id: item.id });
  await showEntry(annotation.id);
  return _.chain(annotation.interests)
    .map('item')
    .filter((item): item is ItemPreviewV1 => isTrendable(item))
    .map((item) => addTrendItem(item))
    .value();
}

/**
 * Helper function that exposes the browse panel and switches to the Journal tab.
 */
export function showJournalTab() {
  tabsetChangeTab('sidebar', 'annotate');
}

/**
 * Closes a journal entry and displays the overview
 */
export function closeEntry() {
  const idToHighlight = sqAnnotationStore.getState().id || sqAnnotationStore.getState().highlightId;
  setExpanded(false);
  setId();
  if (idToHighlight) {
    highlightEntry(idToHighlight);
  }
}

/**
 * Sets the highlightId and then clears it after a short time interval. This is used to highlight the journal entry
 * that had just been open in the journal overview.
 *
 * @param id - a journal entry ID
 */
export function highlightEntry(id: string) {
  // highlighting should be temporary, so we automatically clear the ID after 3 seconds.
  if (highlightCanceller) {
    clearTimeout(highlightCanceller);
    highlightCanceller = undefined;
  }

  if (!_.isUndefined(id)) {
    sqAnnotationStore.actions.setHighlightId(id);
    highlightCanceller = setTimeout(function () {
      sqAnnotationStore.actions.setHighlightId();
    }, 1500);
  }
}

/**
 * Creates an annotation (aka journal entry) with its name and interests. If a selected region is provided then
 * that is used to create a capsule that also becomes one of the interests. The current workbookId and worksheetId
 * are added as interests to all annotations to make it possible to find all annotations created in a particular
 * workbook and worksheet. If repliesTo is supplied, then the annotation is considered a comment on a journal entry
 * and only the name field must be supplied.
 *
 * @param {Object} args - Object container
 * @param {String} args.name - The name of the journal entry
 * @param {String} [args.id] - The ID of the journal entry if saving an already existing entry
 * @param {String} [args.workbookId] - The ID of the journal entry's workbook
 * @param {String} [args.worksheetId] - The ID of the journal entry's worksheet
 * @param {String} [args.description] - The description of the journal entry
 * @param {String} [args.document] - The journal entry document
 * @param {Object[]} [args.inputInterests] - The journal entry interests
 * @param {String} [args.inputInterests[].interestId] - Id of item being annotated
 * @param {String} [args.inputInterests[].detailId] - Id of item inside a set, such as a capsule, being annotated
 * @param {Object[]} [args.ranges] - The start/end pairs of interest. A new interest will be added for each range
 * @param {String} [args.repliesTo] - The ID of the journal entry that this one replies to (only used by comments)
 * @param {Boolean} [args.discoverable] - the annotation's discoverable setting. Used by the frontend to determine
 *   if the links in a journal entry should be parsed and set as actual backend annotation interests.
 * @return {Promise} Promise that resolves with the new annotation
 */
export function save(args: {
  id?: string;
  name: string;
  description?: string;
  document?: string;
  inputInterests?: {
    interestId: string;
    detailId?: string;
    searchIntervalStart?: number;
    searchIntervalEnd?: number;
  }[];
  ranges?: { start: string; end: string }[];
  repliesTo?: string;
  discoverable?: boolean;
  workbookId?: string;
  worksheetId?: string | null;
  annotationCondition?: AnnotationCondition;
}) {
  const id = args.id;
  const name = args.name;
  const description = args.description;
  const document = args.document;
  let inputInterests = args.inputInterests;
  let ranges = args.ranges ?? [];
  const repliesTo = args.repliesTo;
  const discoverable = args.discoverable;

  inputInterests = inputInterests || [];
  ranges = _.map(ranges, function (range: any) {
    return {
      start: moment.utc(+range.start).toISOString(),
      end: moment.utc(+range.end).toISOString(),
    };
  });

  const cancellationGroup = `annotationSave-${id}`;
  const cancelOnServer = true;

  const func =
    !_.isNil(id) && validateGuid(id)
      ? (body: AnnotationInputV1) =>
          sqAnnotationsApi.updateAnnotation(body, { id }, { cancellationGroup, cancelOnServer })
      : (body: AnnotationInputV1) => sqAnnotationsApi.createAnnotation(body);

  return cancelGroup(cancellationGroup, cancelOnServer)
    .then(() => getUserConditionInterests(ranges, args.annotationCondition))
    .then(function (additionalInterests) {
      const interests = (
        _.chain(inputInterests)
          .concat([{ interestId: args.workbookId || sqWorkbenchStore.stateParams.workbookId }])
          .concat(args.worksheetId ? [{ interestId: args.worksheetId }] : [])
          .concat(additionalInterests) as any
      ) // See https://github.com/DefinitelyTyped/DefinitelyTyped/issues/6586
        .uniqWith(_.isEqual)
        .value();
      return func(
        _.omitBy(
          {
            name,
            description,
            document,
            interests,
            repliesTo,
            discoverable,
            type: ANNOTATION_TYPE.JOURNAL,
          },
          _.isNil,
        ) as AnnotationInputV1,
      ).then(({ data: annotation }) => {
        sqAnnotationStore.actions.setAnnotation(id, annotation);
        emitComment(_.chain(interests).map('interestId').uniq().value());
        recomputeRuntimeChartItems();

        return annotation;
      });
    });
}

/**
 * Calls getCapsules with a start and end time that is large enough to include all of the supplied ranges
 *
 * @param  ranges - list of start/end pairs
 * @param  conditionId - Guid of the capsule series to request
 * @return {Promise} - resolves with the capsules
 */
export function getCapsulesOverRanges(ranges: { start: string; end: string }[], conditionId: string) {
  return sqConditionsApi
    .getCapsules({
      id: conditionId,
      start: _.minBy(ranges, 'start')!.start,
      end: _.maxBy(ranges, 'end')!.end,
    })
    .then(({ data: { capsules } }) => {
      // This removes minor date formatting issues between moment and appserver. Namely corrects
      // "2016-10-31T08:25:00Z" to "2016-10-31T08:25:00.000Z
      capsules.forEach((capsule) => {
        capsule.end = moment.utc(capsule.end).toISOString();
        capsule.start = moment.utc(capsule.start).toISOString();
      });

      return capsules;
    });
}

/**
 * Finds the comment capsule series when needed and then creates or finds the capsules that represent them. Then
 * return the list of interests for the capsules matching the ranges provided
 *
 * @param ranges - list of start/end pairs
 * @param annotationCondition - Optional annotation condition from useAnnotationCondition() hook.
 */
export function getUserConditionInterests(
  ranges: { start: string; end: string }[],
  annotationCondition?: AnnotationCondition,
) {
  // If the analysis is read only, then don't add selected ranges because the user does not have permission to
  // create new capsule(s) on the Annotation Capsules condition to represent the selected region.
  if (_.isEmpty(ranges) || !canWriteItem(sqWorkbookStore)) {
    return Promise.resolve([]);
  }

  return findOrCreateUserCondition(annotationCondition).then(({ id: conditionId, condition }) => {
    return getCapsulesOverRanges(ranges, conditionId)
      .then((existingCapsules: CapsuleV1[]) => {
        // Because lodash propagates undefined, the undefined detail ids were the ones we couldn't find
        const newRanges = _.reject(ranges, _.partial(_.find, existingCapsules));

        return _.isEmpty(newRanges)
          ? existingCapsules
          : _.chain(newRanges as CapsuleV1[])
              .reduce((result, range: any) => {
                const milliseconds = parseISODate(range.end).diff(parseISODate(range.start));
                return milliseconds > result ? milliseconds : result;
              }, 0)
              .thru(_.partial(adjustConditionMaxDuration, conditionId, condition?.maximumDuration ?? 0))
              .value()
              .then(() =>
                sqConditionsApi
                  .addCapsules({ capsules: newRanges }, { id: conditionId })
                  .then(_.partial(getCapsulesOverRanges, ranges, conditionId))
                  .then((capsules) => {
                    fetchTableAndChartCapsules();
                    fetchChartCapsules();
                    fetchTimebarCapsules(conditionId);
                    return capsules;
                  }),
              );
      })
      .then((capsules) => {
        const interests = _.map(ranges, (range) => ({
          interestId: conditionId,
          detailId: _.get(_.find(capsules, range), 'id'),
          searchIntervalStart: sqDurationStore.getState().displayRange.start,
          searchIntervalEnd: sqDurationStore.getState().displayRange.end,
        }));

        if (!_.every(interests, 'detailId')) {
          // if the second call to getCapsulesOverRanges doesn't return all the capsules that were created,
          // then _.find wont find all the ranges in `capsules` and we will end up with undefined detailIds
          return Promise.reject('Could not add a comment capsule as a new interest');
        } else {
          return interests;
        }
      });
  });
}

/**
 * Increases the maximumDuration property if it is smaller than a specified interval.
 *
 * @param  {String} id - ID of the condition to update
 * @param  {Number} currentDuration - Current max duration for the condition, in milliseconds
 * @param  {Number} duration - Duration to test, in milliseconds
 * @return {Promise} Promise that resolves when any property changes have been saved
 */
export function adjustConditionMaxDuration(id: string, currentDuration: number, duration: number) {
  if (duration <= currentDuration) {
    return Promise.resolve();
  }

  return sqItemsApi.setProperty({ value: `${duration} ms` }, { id, propertyName: 'Maximum Duration' }).then(() => {
    dispatchAnnotationCondition({ id, maximumDuration: duration });
  });
}

/**
 * Deletes the annotation from the backend, including its associated capsules.
 *
 * Note: This is the ONLY way to delete annotation capsules. Clicking the X on a selected region
 * in the trend does NOT delete capsules - it only clears the selection.
 *
 * @param id - An annotation id
 */
export function deleteFn(id: string) {
  // First, fetch the annotation to get its capsule time ranges
  return sqAnnotationsApi
    .getAnnotation({ id })
    .then((response) => {
      const annotation = response.data;

      // Extract capsule time ranges from interests and find the condition ID
      const capsulesWithConditionId = _.chain(annotation.interests)
        .filter((interest) => interest.capsule && interest.capsule.start && interest.capsule.end && interest.item?.id)
        .map((interest) => ({
          start: interest.capsule!.start,
          end: interest.capsule!.end,
          conditionId: interest.item!.id,
        }))
        .value();

      // Group by condition ID (should all be the same "Annotation Capsules" condition)
      const capsulesByCondition = _.groupBy(capsulesWithConditionId, 'conditionId');

      // Delete capsules for each condition (should typically be just one)
      if (_.size(capsulesByCondition) > 0) {
        const deletionPromises = _.flatMap(capsulesByCondition, (capsules, conditionId) => {
          return _.map(capsules, (capsuleInfo) => {
            // When timestamps are ISO strings, use exact matching to avoid deleting wrong capsules.
            // When timestamps are numeric (nanoseconds), they exceed JavaScript's Number.MAX_SAFE_INTEGER,
            // causing precision loss during JSON parsing. Use a 3ms range to handle ±1ms rounding errors.
            const isString = typeof capsuleInfo.start === 'string' && typeof capsuleInfo.end === 'string';

            if (isString) {
              // ISO strings have no precision loss - use exact matching
              return sqConditionsApi
                .deleteCapsulesByRange({
                  id: conditionId,
                  start: capsuleInfo.start as string,
                  end: capsuleInfo.end as string,
                  exactMatch: true,
                })
                .then(() => conditionId);
            } else {
              // Numeric nanoseconds lose precision - use range matching with ±1ms buffer
              const startMs = Math.floor((capsuleInfo.start as number) / 1000000);
              // With exactMatch=false, API deletes capsules where: capsule.start >= start AND capsule.start < end
              // The interval [startMs-1, startMs+2) includes startMs-1, startMs, and startMs+1
              const rangeStartIso = moment.utc(startMs - 1).toISOString();
              const rangeEndIso = moment.utc(startMs + 2).toISOString();

              return sqConditionsApi
                .deleteCapsulesByRange({
                  id: conditionId,
                  start: rangeStartIso,
                  end: rangeEndIso,
                  // exactMatch=false: delete capsules where capsule.start >= start AND capsule.start < end
                })
                .then(() => conditionId);
            }
          });
        });

        return Promise.all(deletionPromises).then((conditionIds) => {
          return _.compact(_.uniq(conditionIds));
        });
      }

      return [];
    })
    .then((conditionIds) => {
      // Now archive the annotation
      return sqAnnotationsApi.archiveAnnotation({ id }).then(() => conditionIds);
    })
    .then((conditionIds) => {
      if (id === sqAnnotationStore.getState().id) {
        setId();
      }

      fetchAnnotations();
      emitComment([id]);

      // Refresh capsule displays after deletion
      if (conditionIds.length > 0) {
        fetchTableAndChartCapsules();
        fetchChartCapsules();
        conditionIds.forEach((conditionId) => {
          fetchTimebarCapsules(conditionId);
        });
      }
    });
}

/**
 * Sets the annotation ID
 *
 * @param {String} [id] - the ID
 */
export function setId(id?: string) {
  sqAnnotationStore.actions.setId(id);
}

/**
 * Sets the annotation document (which is a string containing sanitized html) in the editor and the store.
 * @param document - the document
 * @param forceUpdate - If the editor supports it, the editor will trigger an update when the document is set
 * @param setCkDocument - If true, sets the internal CKEditor document as well
 */
export function setDocument(document: string, forceUpdate = false, setCkDocument = true) {
  sqAnnotationStore.actions.setDocument(document);
  if (setCkDocument) {
    setHtmlCKEditor(document, 'journalEditor', forceUpdate);
  }
}

/**
 * Sets the isDiscoverable flag, indicating that the Journal entry is being used to annotate something. If
 * isDiscoverable is true, then Journal links in the document will be analyzed and supplied to the /annotations
 * endpoint as the interests and ranges of the annotation.
 *
 * @param {Boolean} isDiscoverable - true if discoverable, false otherwise.
 */
export function setIsDiscoverable(isDiscoverable?: boolean) {
  sqAnnotationStore.actions.setIsDiscoverable(isDiscoverable ?? false);
}

/**
 * Sets the annotation isExpanded flag, which indicates if the journal editor should be displayed in the
 * expanded (i.e. resizeable) view or not.
 *
 * @param expand - true if expanded, false otherwise.
 */
export function setExpanded(expand: boolean) {
  sqAnnotationStore.actions.setExpanded(expand);
}

/**
 * Sets the width of the expanded view
 *
 * @param {Number} width - the width
 */
export function setWidth(width: number) {
  sqAnnotationStore.actions.setWidth(width);
}

/**
 * Sets the height of the expanded view
 *
 * @param {Number} height - the height
 */
export function setHeight(height: number) {
  sqAnnotationStore.actions.setHeight(height);
}

/**
 * Sets the isCommentsExpanded flag, which indicates if the comments area of the journal editor should be
 * displayed as expanded or not.
 *
 * @param {Boolean} expand - true if expanded, false otherwise.
 */
export function setCommentsExpanded(expand: boolean) {
  sqAnnotationStore.actions.setCommentsExpanded(expand);
}

/**
 * Sets the isAnnotatesExpanded flag, which indicates if the annotations area of the journal editor should be
 * displayed as expanded or not.
 *
 * @param {Boolean} expand - true if expanded, false otherwise.
 */
export function setAnnotatesExpanded(expand: boolean) {
  sqAnnotationStore.actions.setAnnotatesExpanded(expand);
}

/**
 * Fetches the annotations that annotate the specified items and dispatches them.
 *
 * WorkbookId is required and has a default of the value from the workstep state parameters. WorksheetId is optional;
 * in Vantage it defaults to `null` (workbook-scoped), otherwise it defaults to the worksheetId from the workstep state
 * parameters.
 *
 * The method finds annotations that are associated with the workbook and worksheet then filters them for those that
 * annotate the worksheet. If worksheetId is null, it will filter for items whose interests include the workbookId.
 *
 * @param [workbookId = sqWorkbenchStore.stateParams.workbookId] - optional workbookId. Supplied when worksheet is
 * initially loaded
 *   by router.
 * @param [worksheetId = sqWorkbookStore.isVantage ? null : sqWorkbenchStore.stateParams.worksheetId] - optional
 *   worksheetId. Supplied when worksheet is initially loaded by router. In Vantage, defaults to `null` so the fetch
 *   stays workbook-scoped.
 *
 * @returns {Promise<AnnotationOutputV1[]>} Promise that is resolved when the annotations have been fetched
 */
export async function fetchAnnotations(
  workbookId: string = sqWorkbenchStore.stateParams.workbookId,
  worksheetId: string | null = sqWorkbookStore.isVantage ? null : sqWorkbenchStore.stateParams.worksheetId,
): Promise<AnnotationOutputV1[] | undefined> {
  // workbookId and worksheetId should always be populated, but may sometimes end up being empty if
  // sqWorkbenchStore.stateParams is not fully initialized. This can happen when reloading a page. This safeguard
  // prevents a second annotation from being created in the case where workbookId and worksheetId are not populated.
  if (!isInWorkbookRouteAndWorkbookLoaded() || isPresentationWorkbookMode() || !workbookId) {
    return Promise.resolve([]);
  }

  try {
    const allAnnotatePromises = _.chain(getAnnotates(workbookId, worksheetId))
      .compact()
      .uniq()
      .chunk(MAX_NUMBER_OF_ANNOTATE_IDS)
      .map((annotates) => sqAnnotationsApi.getAnnotations({ annotates, type: ANNOTATION_TYPE.JOURNAL, limit: 1000 }))
      .value();
    const result = await Promise.all(allAnnotatePromises);
    const annotations = _.chain(result)
      .flatMap(({ data }) => data.items)
      .uniqBy('id')
      .filter((annotation) => {
        return (
          annotation?.discoverable ||
          _.intersection(
            _.map(annotation?.interests, 'item.id'),
            worksheetId ? _.map(sqWorkbookStore.worksheets, 'worksheetId') : [workbookId],
          ).length > 0
        );
      })
      .compact()
      .value();

    sqAnnotationStore.actions.setAnnotations(annotations);
    recomputeRuntimeChartItems();

    return annotations;
  } catch (error) {
    if (isCanceled(error)) {
      throw error;
    }
    if (isApiError(error)) {
      return handleForbidden(error, []);
    }
  }
}

/** Fetches the existing journal(s) for the worksheet (or workbook, for Vantage) directly from the server. */
export const findDefaultJournalsOnServer = async (
  workbookId: string,
  worksheetId: string | null,
): Promise<Annotation[] | null> => {
  const isVantage = sqWorkbookStore.isVantage;
  const target = isVantage ? workbookId : worksheetId;
  if (!target) {
    return null;
  }
  const { data } = await sqAnnotationsApi.getAnnotations({
    annotates: [target],
    type: ANNOTATION_TYPE.JOURNAL,
    limit: 1000,
  });
  const items = (data.items ?? []) as Annotation[];
  return isVantage ? findExclusiveEntry(items, workbookId) : findJournalEntries(items, worksheetId ?? undefined);
};

/** Merges the given journals into the in-memory annotation store, deduping by id. */
export const mergeAnnotationsIntoStore = (annotations: Annotation[]) => {
  if (!annotations.length) {
    return;
  }
  const existing = sqAnnotationStore.getState().annotations;
  sqAnnotationStore.actions.setAnnotations(_.uniqBy([...existing, ...annotations], 'id'));
};

/**
 * If no annotation is in the store and none exist for the current work item. This could be a worksheet for a
 * traditional journal or a workbook for the vantage AI instructions. Open a new annotation and
 * make it display in edit mode on the Journal tab. If there is a single existing annotation, then
 * automatically display it on the Journal tab. If there are more than one journal entries for the worksheet,
 * then close the entry and allow the journal overview to display.
 *
 * @param [workItemId = sqWorkbenchStore.stateParams.worksheetId] - the worksheet ID
 * @param [isExclusive = false] - if true, only show the entry if it is the only one for the worksheet
 * @returns {Promise} a promise that resolves when the display operation is complete
 */
export function displayNewOrExisting(
  workItemId: any = sqWorkbenchStore.stateParams.worksheetId,
  isExclusive = false,
): Promise<any> {
  const worksheetJournalEntries = isExclusive
    ? findExclusiveEntry(sqAnnotationStore.getState().annotations, workItemId)
    : findJournalEntries(sqAnnotationStore.getState().annotations, workItemId);
  if (worksheetJournalEntries.length === 0) {
    // Only display a new entry in edit mode if the user has permission to do so
    if (sqWorkbookStore.effectivePermissions.write) {
      newEntry();
    }
  } else if (worksheetJournalEntries.length === 1) {
    const id = _.get(worksheetJournalEntries, '0.id');
    return showEntry(id, false);
  } else {
    closeEntry();
  }

  return Promise.resolve();
}

/**
 * Fetches the journal document
 *
 * @param {String} id - the ID
 *
 * @returns {Promise} a promise that resolves when the document has been retrieved
 */
export function fetchDocument(id: string): Promise<AnnotationOutputV1> {
  if (!validateGuid(id)) {
    return Promise.resolve({} as AnnotationOutputV1);
  }

  return sqAnnotationsApi
    .getAnnotation({ id })
    .then(({ data }) => data)
    .catch(() => ({}) as AnnotationOutputV1); // Can happen if annotation was deleted
}

/**
 * Finds the "Annotation Capsules" condition without creating it.
 * Returns null if the condition doesn't exist.
 *
 * @param annotationCondition - Optional annotation condition from useAnnotationCondition() hook.
 */
function findUserCondition(annotationCondition?: AnnotationCondition): Promise<string | null> {
  if (annotationCondition) {
    return Promise.resolve(annotationCondition.id ?? null);
  }

  return sqItemsApi
    .searchItems({
      filters: [`name==/${CAPSULE_SET_PREFIX}.*/`],
      scope: sqWorkbenchStore.stateParams.workbookId ? [sqWorkbenchStore.stateParams.workbookId.toString()] : undefined,
      types: [API_TYPES.STORED_CONDITION],
      offset: 0,
      limit: 1,
    })
    .then(({ data }) => {
      if (data.items && data.items.length > 0) {
        const conditionId = data.items[0]!.id;
        // Fetch and cache the metadata for future use
        return sqConditionsApi
          .getCondition({ id: conditionId })
          .then(({ data }) => dispatchAnnotationCondition({ id: data.id, maximumDuration: data.maximumDuration }))
          .then((cond) => cond.id);
      }
      return null;
    });
}

/**
 * Clears all annotations in the workbook and all capsules in the "Annotation Capsules" condition.
 * This is a destructive operation that should only be performed with user confirmation.
 *
 * Only attempts to delete annotations the user has permission to manage. Uses Promise.allSettled
 * to ensure capsule cleanup proceeds even if some annotation deletions fail (e.g., due to permissions).
 *
 * @param annotationCondition - Optional annotation condition from useAnnotationCondition() hook.
 */
export function clearAllAnnotations(
  annotationCondition?: AnnotationCondition,
): Promise<{ annotationsDeleted: number; capsulesCleared: boolean }> {
  const workbookId = sqWorkbenchStore.stateParams.workbookId;

  return findUserCondition(annotationCondition).then((conditionId) => {
    return sqAnnotationsApi
      .getAnnotations({
        annotates: workbookId ? [workbookId] : undefined,
        type: ANNOTATION_TYPE.JOURNAL,
        limit: 1000,
      })
      .then((annotationsResponse) => {
        const annotations = annotationsResponse.data.items || [];
        // Filter to discoverable annotations that the user can manage
        const manageableAnnotations = _.filter(annotations, (annotation) => {
          return annotation.discoverable && canManageItem(annotation);
        });

        // Archive all manageable annotations using allSettled to handle partial failures.
        // This is defensive coding for edge cases: even if some annotations fail to archive
        // (e.g., unexpected permission issues, corrupted data), we still delete ALL capsules
        // to clean up orphaned capsules. This is especially important for customers who have
        // accumulated orphaned capsules over time. Partial cleanup is better than no cleanup.
        const archivePromises = manageableAnnotations.map((annotation) =>
          sqAnnotationsApi.archiveAnnotation({ id: annotation.id! }),
        );

        return Promise.allSettled(archivePromises).then((results) => {
          const successCount = results.filter((result) => result.status === 'fulfilled').length;

          // Delete ALL capsules from the annotation condition
          if (conditionId) {
            return sqConditionsApi.deleteCapsules({ id: conditionId }).then(() => {
              fetchTableAndChartCapsules();
              fetchChartCapsules();
              fetchTimebarCapsules(conditionId);
              return { annotationsDeleted: successCount, capsulesCleared: true };
            });
          }
          return { annotationsDeleted: successCount, capsulesCleared: false };
        });
      })
      .then((result) => {
        fetchAnnotations();
        return result;
      });
  });
}

/**
 * Finds the condition that is used to store user-created capsules from when a user annotates a selected time
 * region. If it does not exist it is created, and the resulting ID and maximumDuration are stored in
 * sqAnnotationStore.annotationCondition.
 *
 * @param annotationCondition - Optional annotation condition from useAnnotationCondition() hook.
 * @returns Promise that resolves with the condition ID and full condition object
 */
export function findOrCreateUserCondition(
  annotationCondition?: AnnotationCondition,
): Promise<{ id: string; condition: AnnotationCondition | undefined }> {
  if (annotationCondition) {
    return Promise.resolve({ id: annotationCondition.id ?? '', condition: annotationCondition });
  }

  return sqItemsApi
    .searchItems({
      filters: [`name==/${CAPSULE_SET_PREFIX}.*/`],
      scope: sqWorkbenchStore.stateParams.workbookId ? [sqWorkbenchStore.stateParams.workbookId.toString()] : undefined,
      types: [API_TYPES.STORED_CONDITION],
      offset: 0,
      limit: 1,
    })
    .then(({ data }) => {
      if (data.items && data.items.length > 0) {
        // Fetch the metadata, so that we know the current maximumDuration and can increase it as needed
        return sqConditionsApi
          .getCondition({ id: data.items[0]!.id })
          .then(({ data }) => dispatchAnnotationCondition({ id: data.id, maximumDuration: data.maximumDuration }));
      }

      // Note: a capsule series is required to have a maximum duration, so we start from a relatively short
      // 1-day duration. The duration is increased as needed each time a new comment is added to this capsule
      // set, such that it is always at least as large as the duration of the longest comment.
      return getDefaultName(CAPSULE_SET_PREFIX, sqWorkbenchStore.stateParams.workbookId)
        .then((name: string) =>
          sqConditionsApi.createCondition({
            name,
            scopedTo: sqWorkbenchStore.stateParams.workbookId,
            maximumDuration: '1 day',
          }),
        )
        .then(({ data }) => dispatchAnnotationCondition(data));
    })
    .then((condition: AnnotationCondition) => {
      const conditionId = condition.id ?? '';
      return sqTrendConditionStore.findItem(conditionId)
        ? Promise.resolve({ id: conditionId, condition })
        : addTrendItem({
            id: conditionId,
            type: API_TYPES.STORED_CONDITION,
          }).then(() => ({ id: conditionId, condition }));
    });
}

/**
 * Locally filters the annotation store to keep only annotations that still reference at least one
 * valid annotate ID (trend items, workbook, or worksheet). Used to avoid a full API re-fetch when
 * items are removed from the trend.
 */
export function removeOrphanedAnnotations(): void {
  const remainingAnnotates = new Set(getAnnotates().map(String));
  const annotations = sqAnnotationStore.getState().annotations.filter((annotation) => {
    if (annotation.discoverable) {
      return true;
    }
    const interestItemIds = _.map(annotation.interests, 'item.id').filter(Boolean).map(String);
    return interestItemIds.some((id) => remainingAnnotates.has(id));
  });
  sqAnnotationStore.actions.setAnnotations(annotations);
  recomputeRuntimeChartItems();
}

/**
 * Gets the ids of all of the items that we want to get annotations for
 *
 * @param [workbookId] - optional workbookId
 * @param [worksheetId] - optional worksheetId
 *
 * @return {String[]} list of ids that can be passed to the Annotations endpoint
 */
export function getAnnotates(
  workbookId: string = sqWorkbenchStore.stateParams.workbookId,
  worksheetId: string | null = sqWorkbenchStore.stateParams.worksheetId,
): string[] {
  return _.chain(getAllItems({}))
    .map('id')
    .concat([workbookId])
    .concat([worksheetId])
    .compact() // Handles the case sqWorkbenchStore.stateParams.workbookId is undefined
    .value();
}

/**
 * Handles journal update messages.
 *
 * @returns that resolves when the journal document has been loaded
 */
export async function onJournalUpdate(): Promise<void> {
  const id = sqAnnotationStore.getState().id ?? '';
  const { document } = await fetchDocument(id);
  // fetchDocument yields an empty object for an unsaved (placeholder id) or deleted annotation; setting the
  // document from that would blank the open journal editor.
  if (_.isNil(document)) {
    return;
  }

  // Discard if the user switched entries while this fetch was in flight
  if (id !== sqAnnotationStore.getState().id) {
    return;
  }

  if (canModifyDocument()) {
    const maybePosition = getCursorPosition('journalEditor');
    setDocument(document);
    maybePosition && setCursorPosition(maybePosition, 'journalEditor');
  } else {
    setDocument(document);
  }
}

/**
 * Attaches a listener to the Socket that handles updating the comments if they changes
 */
export function setupNotifierForAnnotation() {
  onComment((interests) => {
    let targetInterests;
    if (!isWorksheet() || isViewOnlyWorkbookMode()) {
      return;
    }

    targetInterests = _.chain(getAnnotates())
      .concat(_.map(sqAnnotationStore.getState().annotations, 'id'))
      .concat(_.chain(sqAnnotationStore.getState().annotations).flatMap('replies').map('id').value())
      .value();

    // Only the annotation list is refreshed here. Journal documents come over the journal-update channel, which is
    // keyed by the journal that actually changed -- this broadcast only carries the worksheet, so refreshing from it
    // would overwrite whichever entry the recipient happens to have open.
    if (!_.isEmpty(_.intersection(targetInterests, interests ?? []))) {
      fetchAnnotations();
    }
  });
}

/**
 * Dispatches the annotation condition information to the store.
 *
 * @param  {Object} condition - Annotation Condition
 * @param  {String} condition.id - ID of the item
 * @param  {String|Number} condition.maximumDuration - The maximum length of a capsule in the condition.
 *   If this is a number, it is in milliseconds. If a string, it should have a suffix with the units.
 *
 * @returns {Object} Returns the provided condition object
 */
export function dispatchAnnotationCondition(condition: AnnotationCondition) {
  const milliseconds = _.isFinite(condition.maximumDuration)
    ? (condition.maximumDuration as number)
    : parseDuration(condition.maximumDuration as string).asMilliseconds();

  const normalizedCondition = {
    id: condition.id,
    maximumDuration: milliseconds,
  };

  sqAnnotationStore.actions.setAnnotationCondition(normalizedCondition);

  return normalizedCondition;
}

export function setIsCkEnabled(isCkEnabled: boolean) {
  sqAnnotationStore.actions.setIsCkEnabled(isCkEnabled);
}
